/** @format */

import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import mixpanel from "mixpanel-browser";
import { Toast } from "radix-ui";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import outputs from "../amplify_outputs.json";
import "./App.css";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay";
import { OrganizationBootstrap } from "./components/OrganizationBootstrap";
import { ToastViewport } from "./components/ui/Toast";
import EditorOnlyRoute from "./components/EditorOnlyRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminRoute from "./components/SuperAdminRoute";
import { useGlobalLoading } from "./hooks/useGlobalLoading";
import { useOrgRole } from "./hooks/useOrgRole";
import GenerateSOP from "./pages/generate-sop/GenerateSop";
import Home from "./pages/home";
import Login from "./pages/Login/Login";
import "./pages/Login/Login.css";
import ProfileLayout from "./pages/profile/ProfileLayout";
import AccountSettings from "./pages/profile/AccountSettings";
import { OrganizationSettings } from "./pages/organization/OrganizationSettings";
import SopDashboard from "./pages/sop-dashboard/SopDashBoard";
import SopEditor from "./pages/sop-editor/SOPEditor";
import SOPTitle from "./pages/sop-editor/SOPTitle";
import SOPVersions from "./pages/sop-versions/SOPVersions";
import FeedbackPage from "./pages/feedback/FeedbackPage";
import PendingVerificationSOPs from "./pages/sops-pending-verification/PendingVerificationSOPs";
import TestProcedures from "./pages/test-procedures/TestProcedures";
import TrackChangesList from "./pages/track-changes-list/TrackChangesList";
import AcceptInvitation from "./pages/invite/AcceptInvitation";
import RMAAnalysisPage from "./pages/rma-analysis/RMAAnalysisPage";
import RMADatasetPage from "./pages/rma-analysis/RMADatasetPage";
import RMADashboardPage from "./pages/rma-analysis/RMADashboardPage";
import RMAPendingReviewPage from "./pages/rma-analysis/RMAPendingReviewPage";
import useSubscriptionStore from "./store/useSubscriptionStore";
import PostHogIdentitySync from "./components/PostHogIdentitySync";
import DemoAccessBanner from "./components/demo/DemoAccessBanner";
import DemoInvitesAdmin from "./pages/admin/DemoInvitesAdmin";
import DemoAccept from "./pages/demo-accept/DemoAccept";
import {
  FLAG_COMPARE_VERSIONS,
  FLAG_RMA_ANALYSIS,
  FLAG_TRACK_CHANGES,
  useFeatureFlagEnabled,
} from "./lib/featureFlags";

/** Routes where we show global loading overlay and inert: title page + SOP steps editor only. */
function isSopEditorRoute(pathname: string): boolean {
  return /^\/sop\/[^/]+(\/steps)?\/?$/.test(pathname);
}

// function getAuthCookieDomain(): string {
//   const configured = (import.meta.env.VITE_AUTH_COOKIE_DOMAIN || "").trim();
//   if (configured) return configured;

//   const host = window.location.hostname;
//   if (host === "localhost" || host.startsWith("127.") || host.includes(":")) {
//     return host;
//   }

//   if (import.meta.env.PROD && host.endsWith(".manufacture-ai.com")) {
//     return ".manufacture-ai.com";
//   }

//   return host;
// }

Amplify.configure(outputs);
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: window.location.hostname,
    secure: true,
    sameSite: "strict",
    path: "/",
  }),
);

mixpanel.init(import.meta.env.VITE_MIXPANEL_API_KEY, {
  debug: import.meta.env.MODE !== "production",
  track_pageview: true,
  persistence: "localStorage",
  autotrack: true,
});

function AppRoutesWithLoading() {
  const location = useLocation();
  const globalLoading = useGlobalLoading();
  const { canCreateSOP, canManageFeedback, canAccessRMA } = useOrgRole();
  // WORKAROUND: see lib/WORKAROUND_feature_flags.md
  const rmaEnabled = useFeatureFlagEnabled(FLAG_RMA_ANALYSIS);
  const trackChangesEnabled = useFeatureFlagEnabled(FLAG_TRACK_CHANGES);
  const compareVersionsEnabled = useFeatureFlagEnabled(FLAG_COMPARE_VERSIONS);
  const contentRef = useRef<HTMLDivElement>(null);
  const showEditorLoading = isSopEditorRoute(location.pathname);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const shouldInert = showEditorLoading && globalLoading;
    if (shouldInert) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }, [showEditorLoading, globalLoading]);

  return (
    <>
      <OrganizationBootstrap>
        <DemoAccessBanner />
        <div ref={contentRef}>
          <Routes>
            <Route
              index
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate-sop"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={canCreateSOP}>
                    <GenerateSOP />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-procedures"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={canCreateSOP}>
                    <TestProcedures />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/invite/accept" element={<AcceptInvitation />} />
            <Route path="/demo/accept" element={<DemoAccept />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <ProfileLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/settings/organization" replace />} />
              <Route path="account" element={<AccountSettings />} />
              <Route path="organization" element={<OrganizationSettings embedded />} />
              <Route
                path="demo-invites"
                element={
                  <SuperAdminRoute>
                    <DemoInvitesAdmin embedded />
                  </SuperAdminRoute>
                }
              />
            </Route>
            <Route
              path="/sop/:sopId/versions"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={compareVersionsEnabled !== false}>
                    <SOPVersions />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sop-dashboard"
              element={
                <ProtectedRoute>
                  <SopDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/track-changes"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={trackChangesEnabled !== false}>
                    <TrackChangesList />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sops/pending-verification"
              element={
                <ProtectedRoute>
                  <SuperAdminRoute>
                    <PendingVerificationSOPs />
                  </SuperAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={canManageFeedback}>
                    <FeedbackPage />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rma-dashboard"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={rmaEnabled !== false && canAccessRMA}>
                    <RMADashboardPage />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rma/pending-review"
              element={
                <ProtectedRoute>
                  <SuperAdminRoute>
                    <RMAPendingReviewPage />
                  </SuperAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sop/:sopId/rma"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={rmaEnabled !== false && canAccessRMA}>
                    <RMAAnalysisPage />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sop/:sopId/rma/dataset"
              element={
                <ProtectedRoute>
                  <EditorOnlyRoute canAccess={rmaEnabled !== false && canAccessRMA}>
                    <RMADatasetPage />
                  </EditorOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sop/:sopId"
              element={
                <ProtectedRoute>
                  <SOPTitle />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sop/:sopId/steps"
              element={
                <ProtectedRoute>
                  <SopEditor />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </OrganizationBootstrap>
      {showEditorLoading && globalLoading && <GlobalLoadingOverlay />}
    </>
  );
}

function App() {
  const { fetchSubscriptionStatus } = useSubscriptionStore();

  React.useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return (
    <Authenticator.Provider>
      <PostHogIdentitySync />
      <Toast.Provider swipeDirection="right">
        <BrowserRouter>
          <AppRoutesWithLoading />
        </BrowserRouter>
        <ToastViewport />
      </Toast.Provider>
    </Authenticator.Provider>
  );
}

export default App;
